import { Modal, Button, Form } from "react-bootstrap";
import React, { useEffect, useState } from "react";
import "../../modal.css";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { HOSPITAL_CLASSIFICATIONS, getHospitalClassificationText} from "../../enum";
import { useFormik } from "formik";
import * as Yup from "yup";
import usePlacesAutocomplete, { getGeocode, getLatLng } from "use-places-autocomplete";

const hospitalSchema = Yup.object().shape({
  name: Yup.string().required("Hospital name is required"),
  phoneNumber: Yup.string()
    .matches(
      /^(\+251[\s-]?([19][\s-]?\d{1,2}[\s-]?\d{2}[\s-]?\d{2}[\s-]?\d{2}|11[\s-]?\d{3}[\s-]?\d{2}[\s-]?\d{2})|0([19][\s-]?\d{1,2}[\s-]?\d{2}[\s-]?\d{2}[\s-]?\d{2}|11[\s-]?\d{3}[\s-]?\d{2}[\s-]?\d{2}))$/,
      "Invalid phone number format"
    )
    .required("Hospital phone number is required"),
  latitude: Yup.number().min(-90).max(90).required("Latitude is required"),
  longitude: Yup.number().min(-180).max(180).required("Longitude is required"),
  location: Yup.string().required("Hospital Location is required"),
  branchName: Yup.string(), // Made optional
  hospitalsEmail: Yup.string().email("Invalid email"), // Optional
  classificationofHospitals: Yup.string().required("Classification is required"),
  subCity: Yup.string(), // Made optional
  woreda: Yup.string() // Made optional
});

const HospitalForm = ({ mode, showModal, hideModal, initialData = {}, onSuccess }) => {
  const isEditMode = mode === "edit";
  const [isAutomatic, setIsAutomatic] = useState(false);

  const {
    ready,
    value,
    setValue,
    suggestions: { status, data },
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: { componentRestrictions: { country: "ET" } },
    debounce: 300,
  });

  const fetchCoordinates = async (address) => {
    try {
      const results = await getGeocode({ address });
      const { lat, lng } = getLatLng(results[0]);
      formik.setFieldValue("latitude", lat);
      formik.setFieldValue("longitude", lng);
      formik.setFieldValue("location", address);
    } catch (error) {
      toast.error("Failed to fetch coordinates. Please try again.");
    }
  };

  const formik = useFormik({
    initialValues: {
      name: initialData?.name || "",
      phoneNumber: initialData?.phoneNumber || "",
      latitude: initialData?.latitude ?? null,
      longitude: initialData?.longitude ?? null,
      location: initialData?.location || "",
      branchName: initialData?.branchName || "",
      hospitalsEmail: initialData?.hospitalsEmail || "",
      classificationofHospitals: initialData?.classificationofHospitals || "",
      subCity: initialData?.subCity || "",
      woreda: initialData?.woreda || ""
    },
    validationSchema: hospitalSchema,
    onSubmit: async (values) => {
      try {
        const endpoint = isEditMode ? `/hospitals/${initialData.hospital_id}` : "/hospitals";
        const method = isEditMode ? axios.put : axios.post;

        const hospitalData = {
          name: values.name,
          phoneNumber: values.phoneNumber,
          latitude: values.latitude,
          longitude: values.longitude,
          location: values.location,
          branchName: values.branchName,
          hospitalsEmail: values.hospitalsEmail,
          classificationofHospitals: values.classificationofHospitals,
          subCity: values.subCity,
          woreda: values.woreda
        };

        await method(endpoint, hospitalData);
        toast.success(`Hospital ${isEditMode ? "updated" : "added"} successfully.`);
        onSuccess();
        hideModal();
      } catch (error) {
        toast.error(`Failed to ${isEditMode ? "update" : "add"} hospital.`);
        
        if (error.response?.data?.errors) {
          toast.error(error.response.data.errors);
        } else if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        }
      }
    },
  });

  useEffect(() => {
    if (showModal && !isEditMode) {
      formik.resetForm({
        values: {
          name: "",
          phoneNumber: "",
          latitude: null,
          longitude: null,
          location: "",
          branchName: "",
          hospitalsEmail: "",
          classificationofHospitals: "",
          subCity: "",
          woreda: ""
        }
      });
    }
  }, [showModal, isEditMode]);

  useEffect(() => {
    if (!showModal) {
      setValue("");
      clearSuggestions();
    }
  }, [showModal]);

  const handleSuggestionClick = (description) => {
    setValue(description);
    fetchCoordinates(description);
    clearSuggestions();
  };

  return (
    <Modal
      show={showModal}
      backdrop={false}
      onHide={hideModal}
      scrollable
      size="lg"
      style={{ marginTop: "60px", maxHeight: "90vh" }}
    >
      <Modal.Header closeButton>
        <Modal.Title>{isEditMode ? "Edit Hospital" : "Add Hospital"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="form-group">
          <label>Hospital Name</label>
          <input type="text" className="form-control" name="name" {...formik.getFieldProps("name")} />
          {formik.touched.name && formik.errors.name && <div className="error">{formik.errors.name}</div>}
        </div>
        
        <div className="form-group">
          <label>Hospital Email</label>
          <input type="email" className="form-control" name="hospitalsEmail" {...formik.getFieldProps("hospitalsEmail")} />
          {formik.touched.hospitalsEmail && formik.errors.hospitalsEmail && <div className="error">{formik.errors.hospitalsEmail}</div>}
        </div>
        
        <div className="form-group">
          <label>Subcity</label>
          <input type="text" className="form-control" name="subCity" {...formik.getFieldProps("subCity")} />
          {formik.touched.subCity && formik.errors.subCity && <div className="error">{formik.errors.subCity}</div>}
        </div>
        
        <div className="form-group">
          <label>Woreda</label>
          <input type="text" className="form-control" name="woreda" {...formik.getFieldProps("woreda")} />
          {formik.touched.woreda && formik.errors.woreda && <div className="error">{formik.errors.woreda}</div>}
        </div>
        
        <div className="form-group">
          <label>Classification of Hospitals</label>
          <Form.Select
            name="classificationofHospitals"
            value={formik.values.classificationofHospitals}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          >
            <option value=""> select </option>
            {Object.entries(HOSPITAL_CLASSIFICATIONS).map(([key, val]) => (
              <option key={key} value={val}>
                {getHospitalClassificationText(val)}
              </option>
            ))}
          </Form.Select>
          {formik.touched.classificationofHospitals && formik.errors.classificationofHospitals && (
            <div className="error">{formik.errors.classificationofHospitals}</div>
          )}
        </div>

        <div className="form-group">
          <label>Branch Name</label>
          <input type="text" className="form-control" name="branchName" {...formik.getFieldProps("branchName")} />
          {formik.touched.branchName && formik.errors.branchName && <div className="error">{formik.errors.branchName}</div>}
        </div>
        
        <div className="form-group">
          <label>Phone Number</label>
          <input 
            type="text" 
            className="form-control" 
            name="phoneNumber" 
            placeholder="e.g. +251912345678, 0912345678, 0115510500"
            {...formik.getFieldProps("phoneNumber")} 
          />
          {formik.touched.phoneNumber && formik.errors.phoneNumber && (
            <div className="error">{formik.errors.phoneNumber}</div>
          )}
          <small className="form-text text-muted">
            Accepted formats: Mobile (+251 9 12 34 56 78, 09 12 34 56 78) or Landline (+251 11 869 05 53, 011 551 0500)
          </small>
        </div>

        <div className="form-group d-flex align-items-center">
          <label className="mr-3">Location Type: </label>
          <Form.Check
            type="switch"
            id="locationSwitch"
            label={isAutomatic ? "Automatic" : "Manual"}
            checked={isAutomatic}
            onChange={() => setIsAutomatic(!isAutomatic)}
            style={{ transform: "scale(1.1)", marginLeft: "1px" }}
          />
        </div>

        {isAutomatic && (
          <div className="form-group">
            <label>Search Hospital</label>
            <input type="text" className="form-control" value={value} onChange={(e) => setValue(e.target.value)} disabled={!ready} />
            {status === "OK" && (
              <ul className="autocomplete-dropdown">
                {data.map(({ place_id, description }) => (
                  <li key={place_id} onClick={() => handleSuggestionClick(description)}>
                    {description}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="form-group">
          <label>Latitude</label>
          <input type="number" className="form-control" name="latitude" {...formik.getFieldProps("latitude")} disabled={isAutomatic} />
          {formik.touched.latitude && formik.errors.latitude && <div className="error">{formik.errors.latitude}</div>}
        </div>

        <div className="form-group">
          <label>Longitude</label>
          <input type="number" className="form-control" name="longitude" {...formik.getFieldProps("longitude")} disabled={isAutomatic} />
          {formik.touched.longitude && formik.errors.longitude && <div className="error">{formik.errors.longitude}</div>}
        </div>
        
        <div className="form-group">
          <label>Location</label>
          <input type="text" className="form-control" name="location" {...formik.getFieldProps("location")} disabled={isAutomatic} />
          {formik.touched.location && formik.errors.location && <div className="error">{formik.errors.location}</div>}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={hideModal}>
          Cancel
        </Button>
        <Button variant="primary" type="button" disabled={!formik.isValid || formik.isSubmitting} onClick={formik.handleSubmit}>
          {isEditMode ? "Update" : "Submit"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default HospitalForm;